import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

/**
 * A date *range* in one control — start and end picked on a single calendar.
 *
 * Replaces the two From/To boxes: a range is one idea, so it reads better as one
 * field and one calendar than as two pickers you have to keep consistent by hand.
 * First click sets the start, second sets the end; clicking before the start just
 * restarts the range. Empty means "any dates" — no filter.
 *
 * Dates cross the boundary as `YYYY-MM-DD` strings (`{ from, to }`), the shape
 * the ledger filter wants, and all arithmetic is on integer y/m/d — never through
 * `toISOString`, which would shift a day across a timezone.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

const pad = (n) => String(n).padStart(2, '0')
const toISO = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`
const stamp = (c) => Date.UTC(c.y, c.m, c.d)

function parseISO(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? '')
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null
}

function todayParts() {
  const n = new Date()
  return { y: n.getFullYear(), m: n.getMonth(), d: n.getDate() }
}

const shortLabel = (iso) => {
  const p = parseISO(iso)
  return p ? `${p.d} ${MONTHS[p.m].slice(0, 3)}` : ''
}
const longLabel = (iso) => {
  const p = parseISO(iso)
  return p ? `${p.d} ${MONTHS[p.m].slice(0, 3)} ${p.y}` : ''
}

export default function DateRangePicker({
  from = '',
  to = '',
  onChange,
  placeholder = 'any dates',
  closeSignal = 0,
  // Optional. Passing it switches the calendar to staged mode: picks go into
  // a *pending* selection local to this component instead of straight to
  // `onChange`, an in-popover Clear appears that only resets that pending
  // selection, and this fires (with the pending range) only when Apply is
  // pressed — the trigger and the caller's filter stay on the last applied
  // value the whole time. Omit it (ClientLedgers, with its own Apply/Clear
  // beside the trigger and its own external draft state already) and every
  // pick reports straight through `onChange` as before.
  onApply,
}) {
  const staged = Boolean(onApply)
  const [open, setOpen] = useState(false)
  const [hover, setHover] = useState(null)
  // Which side the popover hangs from. Decided when it opens, from the
  // trigger's actual position — a fixed left:0 ran the 252px panel straight
  // off the right edge of the screen for any trigger placed near it (the
  // ActivityDrawer's filter bar, notably).
  const [align, setAlign] = useState('left')
  // Staged mode's in-progress pick, distinct from the applied `from`/`to`
  // props. Unused in the default mode, where the props are the whole story.
  const [pending, setPending] = useState({ from, to })
  const today = todayParts()

  // What the calendar itself picks against and highlights: the pending
  // selection while staged, otherwise just the applied props.
  const dispFrom = staged ? pending.from : from
  const dispTo = staged ? pending.to : to
  const fromP = parseISO(dispFrom)
  const toP = parseISO(dispTo)
  const [view, setView] = useState(() => fromP ?? { y: today.y, m: today.m })
  const rootRef = useRef(null)

  // Every open is a fresh start: jump the visible month to the applied value
  // and drop any pending pick left over from a popover closed without
  // pressing Apply, so a reopen never shows an abandoned edit.
  useEffect(() => {
    if (!open) return
    setView(parseISO(from) ?? { y: today.y, m: today.m })
    if (staged) setPending({ from, to })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Layout effect, not the plain kind — runs before paint, so the popover
  // never flashes open on the wrong side for a frame before flipping.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return
    const rect = rootRef.current.getBoundingClientRect()
    const POP_W = 252
    setAlign(rect.left + POP_W > window.innerWidth - 8 ? 'right' : 'left')
  }, [open])

  // A caller-side action outside this component's own Apply/Clear — the
  // ActivityDrawer's "clear every filter" button, for one — bumps this to
  // dismiss the calendar without going through either.
  useEffect(() => {
    if (closeSignal) setOpen(false)
  }, [closeSignal])

  useEffect(() => {
    if (!open) return
    const onDown = (e) => rootRef.current && !rootRef.current.contains(e.target) && setOpen(false)
    const onKey = (e) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const cells = useMemo(() => {
    const first = new Date(Date.UTC(view.y, view.m, 1))
    const lead = (first.getUTCDay() + 6) % 7
    return Array.from({ length: 42 }, (_, i) => {
      const dt = new Date(Date.UTC(view.y, view.m, 1 - lead + i))
      return { y: dt.getUTCFullYear(), m: dt.getUTCMonth(), d: dt.getUTCDate() }
    })
  }, [view])

  const shift = (delta) => {
    const m = view.m + delta
    setView({ y: view.y + Math.floor(m / 12), m: ((m % 12) + 12) % 12 })
  }

  const pick = (c) => {
    const iso = toISO(c.y, c.m, c.d)
    // A complete range, or none yet → start a new range from this day.
    // Have a start, no end. Earlier than the start → restart; else close it.
    const next =
      !dispFrom || (dispFrom && dispTo)
        ? { from: iso, to: '' }
        : stamp(c) < stamp(fromP)
          ? { from: iso, to: '' }
          : { from: dispFrom, to: iso }
    if (staged) setPending(next)
    else onChange?.(next)
  }

  // The end being previewed: the real end, or the day under the cursor mid-pick.
  const previewEnd = dispTo ? toP : dispFrom && !dispTo && hover ? hover : null
  const lo = fromP && previewEnd ? Math.min(stamp(fromP), stamp(previewEnd)) : null
  const hi = fromP && previewEnd ? Math.max(stamp(fromP), stamp(previewEnd)) : null

  // Apply only once there is an actual change to commit, and only once that
  // change is a complete range (or fully empty) — a lone start date has
  // nothing to apply yet. Clear only once there is something pending to
  // reset; reopening on an already-applied range starts pending non-empty,
  // so Clear is live from the first paint in that case.
  const dirty = pending.from !== from || pending.to !== to
  const canApply = staged && dirty && (!pending.from || pending.to)
  const canClear = staged && Boolean(pending.from || pending.to)

  // The trigger always names the *applied* value, staged or not — it is the
  // one thing on screen while the popover is closed, so it must never show
  // a pending pick nobody has committed yet.
  const label =
    from && to
      ? `${shortLabel(from)} – ${longLabel(to)}`
      : from
        ? `${longLabel(from)} – …`
        : placeholder

  return (
    <div className="dp" ref={rootRef}>
      <button
        type="button"
        className={`dp-trigger${from ? ' has-value' : ''}`}
        style={{ minWidth: 168 }}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" className="dp-ico">
          <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="8" y1="2.5" x2="8" y2="6" />
          <line x1="16" y1="2.5" x2="16" y2="6" />
        </svg>
        <span>{label}</span>
      </button>

      {open && (
        <div className={`dp-pop${align === 'right' ? ' dp-pop-r' : ''}`} role="dialog" onMouseLeave={() => setHover(null)}>
          <div className="dp-head">
            <button type="button" className="dp-nav" onClick={() => shift(-1)} aria-label="Previous month">‹</button>
            <span className="dp-title">{MONTHS[view.m]} {view.y}</span>
            <button type="button" className="dp-nav" onClick={() => shift(1)} aria-label="Next month">›</button>
          </div>

          <div className="dp-grid dp-dow">
            {WEEKDAYS.map((w) => <span key={w} className="dp-wd">{w}</span>)}
          </div>

          <div className="dp-grid">
            {cells.map((c, i) => {
              const outside = c.m !== view.m
              const st = stamp(c)
              const isToday = c.y === today.y && c.m === today.m && c.d === today.d
              const isStart = fromP && st === stamp(fromP)
              const isEnd = toP && st === stamp(toP)
              const inRange = lo !== null && st > lo && st < hi
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(c)}
                  onMouseEnter={() => setHover(c)}
                  className={
                    'dp-day' +
                    (outside ? ' dp-out' : '') +
                    (isToday ? ' dp-today' : '') +
                    (inRange ? ' dp-inrange' : '') +
                    (isStart ? ' dp-start' : '') +
                    (isEnd ? ' dp-end' : '') +
                    (isStart || isEnd ? ' dp-sel' : '')
                  }
                >
                  {c.d}
                </button>
              )
            })}
          </div>

          <div className="dp-foot">
            <span className="dp-hint">{dispFrom && !dispTo ? 'Now pick the end date' : 'Pick a start date'}</span>
            {staged && (
              <span className="dp-actions">
                {/* Resets only the pending pick and stays open — Clear does not
                    touch the applied filter, so the currently-filtered list
                    stays exactly as it was until Apply is pressed. Pressing
                    Apply right after Clear (with nothing picked) is how an
                    already-applied date range actually gets removed. */}
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={!canClear}
                  onClick={() => setPending({ from: '', to: '' })}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={!canApply}
                  onClick={() => {
                    onApply(pending)
                    setOpen(false)
                  }}
                >
                  Apply
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
